import type { FC } from 'react';
import { useMemo, useRef, useState, useCallback } from 'react';
import type { Location } from 'react-router';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Box, Button, Link, Tab, Tabs, Typography } from '@mui/material';
import { useRuleDetails, useUpdateRule, useCreateRule } from '../api';
import { notificationChannelOptions } from '../../api';
import { DiscardChangesDialog, FilterGroupsContainer, RuleEditTitle, TabViewContainer } from '../../components';
import RuleEditBasicForm from './RuleEditBasicForm';
import type { FieldValues, UseFormReset } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ruleEditSchema from '../validation/ruleEdit.schema';
import { observer } from 'mobx-react-lite';
import filterStore from '../stores/filterStore';
import tasksFormStore from '../stores/tasksFormStore';
import { isEmpty, omit } from 'lodash-es';
import RuleEditTasksForm from '../../components/RuleEditTasksForm';
import RuleScriptDialog from '../../components/RuleScriptDialog';
import { useEnqueueSnackbar, useModal, useScrollIntoTabView } from 'hooks';
import TimeTableContainer from '../../components/TimeTableContainer';
import timeTableStore from '../stores/timeTableStore';
import RuleUIFormActionBlock from '../../components/RuleUIFormActionBlock';
import CircularSpinner from 'components/common/CircularSpinner';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RuleUpdatedMessage from '../../components/RuleUpdatedMessage';
import { useRuleNavigation } from '../../hooks';
import { useValidate } from '../../utils/useValidate';
import type { BasicFormValues } from '../types';
import type { LevelValue, RuleStatusValue, RuleTypeMutation } from '../../types';
import { normalizeObject } from '../../utils';
import useRuleBlocker from '../../hooks/useRuleBlocker';
import getFacebookInitialValues from '../utils/getFacebookInitialValues';
import useHydrateRuleData from '../../hooks/useHydrateRuleData';
import { OpenInNew } from '@mui/icons-material';
import { tasksArraySchema } from '../../validation/tasks.schema';
import type { RuleTaskEntry } from '../../types';
import { useSuspenseQuery } from '@tanstack/react-query';

interface RuleEditProps {
  mode: RuleTypeMutation;
}

const EditFacebookRule: FC<RuleEditProps> = observer(({ mode }) => {
  const isModeRead = mode === 'read';
  const isModeDuplicate = mode === 'duplicate';
  const isModeEdit = mode === 'edit';
  const { ruleId } = useParams<{ ruleId: string }>();
  const { data: rule } = useRuleDetails({ id: Number(ruleId) });
  const navigate = useNavigate();
  const { state: stateLocation } = useLocation() as Location<{ isDuplicated?: boolean }>;
  const { isOpen, open, close } = useModal();
  const { data: notificationChannelsData } = useSuspenseQuery(notificationChannelOptions);
  const { onShowAlert } = useEnqueueSnackbar();
  const [isChangesApplied, setIsChangesApplied] = useState(false);
  const { ruleListURL, ruleEditURL } = useRuleNavigation();

  const getNotificationChannelsDTO = (ids: string[]) => {
    return notificationChannelsData.filter((nc) => ids.includes(nc.id));
  };

  const scrollContainerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { tab, handleChangeTab } = useScrollIntoTabView(scrollContainerRef, contentRef);

  const getInitialValues = useMemo(() => getFacebookInitialValues(rule, mode), [rule, mode]);

  const formMethods = useForm<BasicFormValues>({
    // @ts-expect-error type correction
    defaultValues: getInitialValues,
    // @ts-expect-error type correction
    resolver: yupResolver(ruleEditSchema),
    shouldFocusError: true,
    context: {
      duplicateMode: mode === 'duplicate',
      initialRuleName: rule.name,
    },
  });

  const { handleSubmit, reset, formState, watch } = formMethods;

  const level = watch('level');

  const validate = useValidate({ isDirty: formState.isDirty, ruleLevel: level as LevelValue });

  const blocker = useRuleBlocker({ rule, isDirty: formState.isDirty, isRead: mode === 'read' });

  const isBlocked = blocker.state === 'blocked';
  useHydrateRuleData({ rule, reset: reset as unknown as UseFormReset<FieldValues>, mode });

  const { mutateAsync: updateRule, isPending: isUpdating } = useUpdateRule({
    config: {
      onSuccess: () => {
        onShowAlert('Changes saved successfully', { delayAlert: 3500 });
        setIsChangesApplied((v) => !v);
      },
    },
  });

  const { mutateAsync: duplicateRule, isPending: isDuplicating } = useCreateRule({
    config: {
      onSuccess: (data) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
        navigate(ruleEditURL(data.id), { replace: true, state: { skipBlocker: true, isDuplicated: true } });
        onShowAlert('Rule duplicated', { delayAlert: 3500 });
      },
    },
  });

  const getSubmitPayload = async ({
    ruleStatus,
    description,
    project,
    groupId,
    level,
    notificationLevel,
    name,
    notificationChannels,
    facebookAdAccountIds,
    notificationMessage,
    networkId,
    actionTypesIds,
  }: BasicFormValues) => {
    const isValid = await validate(rule);
    if (!isValid) return;

    const { id, team_id } = rule;

    const tasks = tasksArraySchema.cast(tasksFormStore.tasksFormValues) as RuleTaskEntry[];
    return {
      level: level as LevelValue,
      team_id,
      id,
      project_id: project,
      name,
      status: ruleStatus as RuleStatusValue,
      description,
      group_id: Number(groupId),
      network_id: networkId,
      action_types_ids: actionTypesIds,
      data: {
        ...omit(rule.data, timeTableStore.scheduleInterval ? 'timetable' : 'schedule_interval'),
        filters: filterStore.filterGroups,
        ...(timeTableStore.scheduleInterval
          ? { schedule_interval: timeTableStore.scheduleInterval }
          : { timetable: timeTableStore.timeTableDTO }),
        tasks,
        name,
        description,
        level: level as LevelValue,
        notification_channels: getNotificationChannelsDTO(notificationChannels),
        facebook_ad_account_ids: facebookAdAccountIds.map((id) => String(id).trim()),
        notification_level: notificationLevel,
        notification_message:
          (notificationMessage?.title ?? notificationMessage?.body)
            ? {
                title: notificationMessage?.title ?? '',
                body: notificationMessage?.body ?? '',
              }
            : null,
      },
    };
  };

  const onSubmit = async (data: BasicFormValues) => {
    const payload = await getSubmitPayload(data);
    if (payload) {
      if (isModeEdit) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { network_id, action_types_ids, ...restParams } = payload;
        await updateRule(restParams);
        return;
      }

      if (isModeDuplicate || isModeRead) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...restParams } = payload;
        await duplicateRule({
          ...restParams,
          network_id: rule.network_id,
          action_types_ids: rule.action_types.map((at) => Number(at.id)),
        });
        return;
      }
    }
  };

  const onDiscard = () => {
    if (isBlocked) {
      blocker.proceed();
    }
    reset();
    JSON.stringify(normalizeObject(rule.data), null, 2);
    close();
    navigate(ruleListURL);
  };

  const onClose = () => {
    if (isBlocked) {
      blocker.reset();
    }
    close();
  };

  const openLogs = () => {
    window.open(rule.logs_s3_folder_url, '_blank');
  };

  if (isDuplicating) {
    return <CircularSpinner title="Duplicating rule" />;
  }

  return (
    <FormProvider {...formMethods}>
      <DiscardChangesDialog isOpen={isOpen || isBlocked} onClose={onClose} onDiscard={onDiscard} />
      <RuleScriptDialog isOpen={isOpen} onClose={close} rule={rule} />
      <RuleUIFormActionBlock
        mode={['update', 'create-to-some']}
        project={{ id: rule.project_id, name: rule.project }}
        isPending={isUpdating || isDuplicating}
        onDiscard={onDiscard}
        hasChanges
        onSave={handleSubmit(onSubmit)}
        submitTitle={isModeDuplicate ? 'Duplicate' : 'Save changes'}
      />
      <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <RuleEditTitle name={rule.name} status={rule.status} state={rule.state} mode={mode} />
          {isChangesApplied && <RuleUpdatedMessage text="Rule will be updated in 10 minutes or less." />}
          {stateLocation?.isDuplicated && (
            <RuleUpdatedMessage text="New rule has been created. It will be available to perform in 10 minutes or less." />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tab} onChange={handleChangeTab} variant="scrollable" scrollButtons="auto">
              <Tab value={'basics'} label="Basics" />
              <Tab value={'filters'} label="Filters" />
              <Tab value={'tasks'} label="Tasks" />
              <Tab value={'timetable'} label="Timetable" />
            </Tabs>

            <Box>
              <Button
                component={Link}
                href={'https://welltech.atlassian.net/wiki/spaces/MA1/pages/4971266090/User+Rule+builder+in+progress'}
                startIcon={<OpenInNew />}
                variant={'text'}
                color={'primary'}
                size={'medium'}
                target={'_blank'}
                sx={{ textTransform: 'none' }}
              >
                Setup guide
              </Button>
              <Button sx={{ textDecoration: 'underline', textTransform: 'inherit' }} onClick={openLogs}>
                <OpenInNewIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Logs in S3</Typography>
              </Button>
              <Button sx={{ textDecoration: 'underline', textTransform: 'inherit' }} onClick={open}>
                Rule script
              </Button>
            </Box>
          </Box>
        </Box>
        <Box
          ref={scrollContainerRef}
          sx={{ display: 'flex', flexGrow: 1, position: 'relative', overflow: 'auto', mr: -3 }}
        >
          <Box
            ref={contentRef}
            sx={{ display: 'flex', flexDirection: 'column', position: 'absolute', gap: 3, width: '100%', pr: 3 }}
          >
            <TabViewContainer id="basics">
              <RuleEditBasicForm rule={rule} />
            </TabViewContainer>
            {!isEmpty(filterStore.filterGroups) && (
              <TabViewContainer id="filters">
                <FilterGroupsContainer />
              </TabViewContainer>
            )}
            <TabViewContainer id="tasks">
              <RuleEditTasksForm />
            </TabViewContainer>
            <TabViewContainer id="timetable">
              <TimeTableContainer scheduledTimezone={rule.data.schedule_timezone} />
            </TabViewContainer>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
});

export default EditFacebookRule;
